/**
 * Azure Icons Registry
 *
 * Provides official Azure service icons from local SVG assets.
 * Uses explicit imports with SVGR for optimal tree-shaking.
 */

import type { ComponentType, ReactNode } from 'react';

// Explicit SVG imports with SVGR
import AzureADB2C from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-ad-b2c.svg?react';
import AzureAKS from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-aks.svg?react';
import AzureAPIManagement from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-api-management.svg?react';
import AzureAppService from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-app-service.svg?react';
import AzureBlobStorage from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-blob-storage.svg?react';
import AzureCosmosDB from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-cosmos-db.svg?react';
import AzureDefender from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-defender.svg?react';
import AzureDevOps from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-devops.svg?react';
import AzureEntraID from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-entra-id.svg?react';
import AzureFunctions from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-functions.svg?react';
import AzureGeneric from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-generic.svg?react';
import AzureKeyVault from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-key-vault.svg?react';
import AzureLoadBalancer from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-load-balancer.svg?react';
import AzureLogicApps from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-logic-apps.svg?react';
import AzurePipelines from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-pipelines.svg?react';
import AzureRepos from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-repos.svg?react';
import AzureSentinel from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-sentinel.svg?react';
import AzureSQLDatabase from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-sql-database.svg?react';
import AzureVirtualMachine from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-virtual-machine.svg?react';
import AzureVirtualNetwork from '@/sections/attacks/attackPaths/components/icons/bundles/azure/azure-virtual-network.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { AZURE_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type AzureIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from AZURE_ICONS names to SVGR components.
 */
const azureIconComponents: Partial<
  Record<(typeof AZURE_ICONS)[number], AzureIconComponent>
> = {
  Functions: AzureFunctions,
  BlobStorage: AzureBlobStorage,
  CosmosDB: AzureCosmosDB,
  SQLDatabase: AzureSQLDatabase,
  AKS: AzureAKS,
  AppService: AzureAppService,
  VirtualMachine: AzureVirtualMachine,
  VirtualNetwork: AzureVirtualNetwork,
  LoadBalancer: AzureLoadBalancer,
  EntraID: AzureEntraID,
  KeyVault: AzureKeyVault,
  Sentinel: AzureSentinel,
  Defender: AzureDefender,
  LogicApps: AzureLogicApps,
  APIManagement: AzureAPIManagement,
  DevOps: AzureDevOps,
  Repos: AzureRepos,
  Pipelines: AzurePipelines,
  ADB2C: AzureADB2C,
  Generic: AzureGeneric,
};

/**
 * Human-readable labels for Azure icons.
 */
const azureIconLabels: Partial<Record<(typeof AZURE_ICONS)[number], string>> = {
  Functions: 'Azure Functions',
  BlobStorage: 'Azure Blob Storage',
  CosmosDB: 'Azure Cosmos DB',
  SQLDatabase: 'Azure SQL Database',
  AKS: 'Azure Kubernetes Service',
  AppService: 'Azure App Service',
  VirtualMachine: 'Azure Virtual Machine',
  VirtualNetwork: 'Azure Virtual Network',
  LoadBalancer: 'Azure Load Balancer',
  EntraID: 'Microsoft Entra ID',
  KeyVault: 'Azure Key Vault',
  Sentinel: 'Microsoft Sentinel',
  Defender: 'Microsoft Defender',
  LogicApps: 'Azure Logic Apps',
  APIManagement: 'Azure API Management',
  DevOps: 'Azure DevOps',
  Repos: 'Azure Repos',
  Pipelines: 'Azure Pipelines',
  ADB2C: 'Azure AD B2C',
  Generic: 'Azure (Generic)',
};

// ============================================================================
// Azure Renderer Implementation
// ============================================================================

/**
 * Azure icon renderer for the attack graph icon system.
 */
const azureRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent =
      azureIconComponents[name as (typeof AZURE_ICONS)[number]];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (AZURE_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent =
      azureIconComponents[name as (typeof AZURE_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedAzureIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    AZURE_ICONS.map(name => {
      const IconComponent = azureIconComponents[name];
      return {
        id: `azure:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: azureIconLabels[name] ?? name,
        category: 'azure',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('azure', azureRenderer);

export { azureRenderer };
